import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlertCircle, Mail, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { reportFormError } from "@/lib/form-feedback";


import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { subscribeToNewsletter } from "@/lib/newsletter.functions";
import { goldConfetti } from "@/lib/confetti";

import { FlagIcon } from "@/components/site/FlagIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NEWSLETTER_LOCALES } from "@/lib/newsletter";
import type { NewsletterLocale, PageLocale } from "@/lib/newsletter";

const LOCALE_FLAG: Record<NewsletterLocale, { code: string; emoji: string }> = {
  sv: { code: "se", emoji: "🇸🇪" },
  en: { code: "gb", emoji: "🇬🇧" },
  es: { code: "es", emoji: "🇪🇸" },
};

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

const CELEBRATE: Record<PageLocale, { title: string; sub: string }> = {
  sv: { title: "Tack så mycket!", sub: "Nu är du med i gänget – vi ses i inkorgen 🌱" },
  en: { title: "Thank you so much!", sub: "You're on the list – see you in your inbox 🌱" },
  es: { title: "¡Muchísimas gracias!", sub: "Ya formas parte – nos vemos en tu correo 🌱" },
};




const COPY = {
  sv: {
    heading: "Prenumerera på skolans utskick",
    intro:
      "Få nya blogginlägg och skolans nyhetsbrev direkt i inkorgen. Du väljer själv vad du vill ha och kan avsluta när du vill.",
    email: "E-postadress",
    name: "Namn (frivilligt)",
    blog: "Nya blogginlägg",
    blogHint: "Ett mejl när vi publicerar något nytt – om skolan, livet i Estepona och flytten hit.",
    newsletter: "Nyhetsbrev från skolan",
    newsletterHint: "Terminsnyheter, viktiga datum, öppet hus och anmälan – några gånger per termin.",
    frequency: "Inga massutskick. Avsluta med ett klick i varje mejl.",
    submit: "Prenumerera",
    sending: "Skickar…",
    done: "Tack! Din prenumeration är registrerad.",
    doneText: "Vi har skickat en bekräftelse till din e-post med en länk för att avsluta när du vill.",
    error: "Något gick fel. Försök igen om en stund.",
    consent:
      "Vi använder din adress enbart för de utskick du valt. Läs mer i vår integritetspolicy.",
    language: "Språk på utskicken",
    choose: "Välj vad du vill få",
  },
  en: {
    heading: "Subscribe to school updates",
    intro:
      "Get new blog posts and the school newsletter straight to your inbox. You choose what you receive and can unsubscribe at any time.",
    email: "Email address",
    name: "Name (optional)",
    blog: "New blog posts",
    blogHint: "One email when we publish something new about the school, Estepona and moving here.",
    newsletter: "School newsletter",
    newsletterHint: "Term news, key dates, open house and admissions – a few times each term.",
    frequency: "No spam. Unsubscribe with one click in every email.",
    submit: "Subscribe",
    sending: "Sending…",
    done: "Thank you! Your subscription is registered.",
    doneText: "We sent a confirmation to your inbox with a link to unsubscribe whenever you like.",
    error: "Something went wrong. Please try again shortly.",
    consent: "We only use your address for the emails you selected. See our privacy policy for details.",
    language: "Language for your emails",
    choose: "Choose what you want",
  },
  es: {
    heading: "Suscríbete a las novedades del colegio",
    intro:
      "Recibe nuevas entradas del blog y el boletín del colegio directamente en tu correo. Tú eliges qué quieres recibir y puedes darte de baja cuando quieras.",
    email: "Correo electrónico",
    name: "Nombre (opcional)",
    blog: "Nuevas entradas del blog",
    blogHint: "Un correo cuando publiquemos algo nuevo sobre el colegio, Estepona o la vida aquí.",
    newsletter: "Boletín del colegio",
    newsletterHint: "Noticias del trimestre, fechas importantes, jornadas de puertas abiertas y admisiones.",
    frequency: "",
    submit: "Suscribirse",
    sending: "Enviando…",
    done: "¡Gracias! Tu suscripción está registrada.",
    doneText: "Te hemos enviado una confirmación por correo con un enlace para darte de baja cuando quieras.",
    error: "Algo ha salido mal. Inténtalo de nuevo en unos minutos.",
    consent: "Al suscribirte, aceptas que usemos tus datos para enviarte las comunicaciones elegidas. Consulta nuestra política de privacidad.",
    language: "Idioma de los correos",
    choose: "Elige qué quieres recibir",
  },
} as const;

const FOOTER_COPY = {
  sv: {
    title: "Nyhetsbrev och blogg",
    intro: "Några utskick per termin med nyheter från skolan och nya blogginlägg. Avsluta när du vill.",
  },
  en: {
    title: "Newsletter and blog",
    intro: "A few emails each term with school news and new blog posts. Unsubscribe whenever you like.",
  },
  es: {
    title: "Boletín y blog",
    intro: "Algunos correos cada trimestre con noticias del colegio y nuevas entradas del blog. Date de baja cuando quieras.",
  },
} as const;

export function SubscribeForm({
  locale,
  source = "",
  compact = false,
}: {
  locale: PageLocale;
  source?: string;
  compact?: boolean;
}) {
  const t = COPY[locale];
  const subscribe = useServerFn(subscribeToNewsletter);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [wantsBlog, setWantsBlog] = useState(true);
  const [wantsNewsletter, setWantsNewsletter] = useState(true);
  const [company, setCompany] = useState("");
  const [done, setDone] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [mailLocale, setMailLocale] = useState<NewsletterLocale>(locale);

  useEffect(() => setMailLocale(locale), [locale]);

  const emailError =
    emailTouched && !email.trim()
      ? locale === "sv"
        ? "E-postadress krävs."
        : locale === "es" ? "El correo electrónico es obligatorio." : "Email address is required."
      : emailTouched && !isValidEmail(email)
        ? locale === "sv"
          ? "Ange en giltig e-postadress."
          : locale === "es" ? "Introduce una dirección de correo válida." : "Please enter a valid email address."
        : null;

  const mutation = useMutation({
    mutationFn: () =>
      subscribe({
        data: { email, name, locale: mailLocale, wantsBlog, wantsNewsletter, source, company },
      }),
    onSuccess: () => {
      setDone(true);
      toast.success(CELEBRATE[locale].title, { id: "subscribe", description: CELEBRATE[locale].sub });
      void goldConfetti();
    },
    onError: (e: Error) => toast.error(e.message || t.error, { id: "subscribe" }),
  });

  if (done) {
    return (
      <div className="animate-scale-in rounded-xl border border-primary/30 bg-accent/50 p-8 text-center">
        <div className="mx-auto flex size-14 items-center justify-center">
          <PartyPopper className="size-7 text-primary" aria-hidden />
        </div>
        <p className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl">{CELEBRATE[locale].title}</p>
        <p className="mt-2 text-base font-medium text-primary">{CELEBRATE[locale].sub}</p>
        <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">{t.doneText}</p>
      </div>
    );
  }

  const fieldLabel = "text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground";

  return (
    <form
      noValidate
      className={
        compact
          ? "space-y-6"
          : "space-y-6 rounded-xl border border-border bg-background p-6 shadow-[0_1px_0_rgba(0,0,0,0.03)] sm:p-7"
      }
      onSubmit={(e) => {
        e.preventDefault();
        setEmailTouched(true);
        if (!isValidEmail(email)) {
          reportFormError(
            !email.trim()
              ? locale === "sv"
                ? "E-postadress krävs."
                : locale === "es" ? "El correo electrónico es obligatorio." : "Email address is required."
              : locale === "sv"
                ? "Ange en giltig e-postadress."
                : locale === "es" ? "Introduce una dirección de correo válida." : "Please enter a valid email address.",
            { anchorId: "subscribe-email-field", toastId: "subscribe" },
          );
          return;
        }
        if (!wantsBlog && !wantsNewsletter) {
          reportFormError(
            locale === "sv"
              ? "Välj minst en typ av utskick."
              : locale === "es" ? "Elige al menos un tipo de correo." : "Choose at least one type of email.",
            { anchorId: "subscribe-choices", toastId: "subscribe" },
          );
          return;
        }
        mutation.mutate();
      }}

    >
      {!compact ? (
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <Mail className="size-5 text-primary" aria-hidden />
            {t.heading}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.intro}</p>
        </div>
      ) : null}

      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        className="hidden"
      />

      <div className={compact ? "grid gap-9 md:grid-cols-2 lg:grid-cols-[1.15fr_1fr_.85fr] lg:gap-12" : "grid gap-x-10 gap-y-8 md:grid-cols-2"}>
        <div className="space-y-5">
          {compact ? (
            <div>
              <h3 className="font-display text-2xl font-semibold tracking-tight">{FOOTER_COPY[locale].title}</h3>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">{FOOTER_COPY[locale].intro}</p>
            </div>
          ) : null}
          <p className={fieldLabel}>{t.choose}</p>
          <div id="subscribe-choices" className="space-y-4">
            {[
              { key: "blog" as const, checked: wantsBlog, set: setWantsBlog, label: t.blog, hint: t.blogHint },
              {
                key: "newsletter" as const,
                checked: wantsNewsletter,
                set: setWantsNewsletter,
                label: t.newsletter,
                hint: t.newsletterHint,
              },
            ].map((opt) => (
              <label key={opt.key} className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={opt.checked}
                  onCheckedChange={(v) => opt.set(v === true)}
                  className="mt-[3px] size-5 rounded-md"
                />
                <span className="min-w-0">
                  <span className="block text-sm leading-5 font-semibold">{opt.label}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                    {opt.hint}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div id="subscribe-email-field" className="space-y-1.5">

            <Label htmlFor="subscribe-email" className={fieldLabel}>
              {t.email}
            </Label>
            <Input
              id="subscribe-email"
              type="email"
              autoComplete="email"
              maxLength={255}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              aria-invalid={emailError ? true : undefined}
              className={`h-11 rounded-xl bg-background/60 ${emailError ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
            />
            {emailError ? (
              <span className="mt-1.5 flex items-start gap-1.5 text-xs leading-relaxed text-destructive">
                <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
                <span>{emailError}</span>
              </span>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subscribe-name" className={fieldLabel}>
              {t.name}
            </Label>
            <Input
              id="subscribe-name"
              autoComplete="name"
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11 rounded-xl bg-background/60"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subscribe-language" className={fieldLabel}>
              {t.language}
            </Label>
            <Select value={mailLocale} onValueChange={(v) => setMailLocale(v as NewsletterLocale)}>
              <SelectTrigger id="subscribe-language" className="h-11 w-full rounded-xl bg-background/60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NEWSLETTER_LOCALES.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    <span className="inline-flex h-full items-center gap-2 leading-none">
                      <FlagIcon code={LOCALE_FLAG[l.value].code} emoji={LOCALE_FLAG[l.value].emoji} size={18} />
                      {l.nativeLabel}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </div>

        {compact ? (
          <div className="space-y-4 lg:pt-6">
            <Button type="submit" size="lg" className="h-11 w-full rounded-full px-7" disabled={mutation.isPending}>
              {mutation.isPending ? t.sending : t.submit}
            </Button>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t.consent}{" "}
              <Link to={(locale === "sv" ? "/integritetspolicy" : locale === "en" ? "/en/privacy-policy" : "/es/politica-de-privacidad") as never} className="link-quiet hover:text-primary">
                {locale === "sv" ? "Läs policyn." : locale === "es" ? "Leer la política." : "Read the policy."}
              </Link>
            </p>
          </div>
        ) : (
          <div className="space-y-4 md:col-start-2">
            <Button type="submit" size="lg" className="h-11 w-full rounded-full px-9" disabled={mutation.isPending}>
              {mutation.isPending ? t.sending : t.submit}
            </Button>
            <p className="text-xs leading-relaxed text-muted-foreground">{t.consent}</p>
          </div>
        )}
      </div>
    </form>
  );
}

